import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type Activity,
  type Trip,
  type User,
} from "@travelix/core";

import { useAuth } from "@/lib/auth-provider";
import { isLikelyBrokenCoverUrl } from "@/lib/cover-url";
import {
  repairTripSummaryMiniature,
  getTripById,
  getTripActivities,
  unwrap,
  queryKeys,
} from "@/lib/api";

export function useTrip(tripId: string | undefined) {
  return useQuery<Trip>({
    queryKey: queryKeys.trip(tripId),
    enabled: !!tripId,
    queryFn: async () => {
      return unwrap(getTripById(tripId!));
    },
  });
}

/**
 * Read-through fallback for the dashboard cover.
 *
 * The dashboard renders from users/{email}.trips[].miniature — no trip reads.
 * When that miniature is null (v2-created trip, no cover set) or a v1 leftover
 * that isLikelyBrokenCoverUrl() rejects, we fetch trip.coverImage lazily and
 * fall back to it so a manually-updated cover shows up on the card.
 *
 * The queryKey is intentionally the same as useTrip's, so:
 *   • Cards with a valid summary miniature never hit the network.
 *   • Cards that do fetch prime the cache for the trip detail route.
 *   • Multiple cards for the same trip dedupe to one request.
 *
 * See D-015 in docs/decisions.md for why v2 has no photo write path — every
 * cover update happens out-of-band, so summaries drift.
 */
export function useTripCoverFallback(
  tripId: string,
  enabled: boolean,
): { data: string | null | undefined } {
  const qc = useQueryClient();
  const { user } = useAuth();
  const email = user?.email?.toLowerCase() ?? null;

  const q = useQuery<Trip, Error, string | null>({
    queryKey: queryKeys.trip(tripId),
    enabled: enabled && !!tripId,
    queryFn: async () => {
      return unwrap(getTripById(tripId));
    },
    select: (t) => t.coverImage ?? null,
    // Covers rarely change — five minutes keeps the dashboard cheap even
    // when the user pans back and forth between it and trip detail.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Backfill the user summary so this fallback fires ONCE per stale trip,
  // ever — subsequent sessions have miniature set and skip the trip read.
  // Best-effort: on error we just leave the summary stale and re-repair
  // next time; the display path already works via the runtime fallback.
  useEffect(() => {
    if (!enabled || !email || !tripId) return;
    const cover = q.data;
    if (!cover || isLikelyBrokenCoverUrl(cover)) return;

    // Optimistically patch the cached user doc so the fallback query
    // disables itself on the next render — no extra render loop, no
    // waiting on the write.
    qc.setQueryData<User | undefined>(queryKeys.user(email), (prev) => {
      if (!prev) return prev;
      const trips = prev.trips ?? [];
      const idx = trips.findIndex((t) => t.id === tripId);
      if (idx < 0 || trips[idx]!.miniature === cover) return prev;
      const next = trips.map((t, i) => (i === idx ? { ...t, miniature: cover } : t));
      return { ...prev, trips: next };
    });

    void repairTripSummaryMiniature(email, tripId, cover);
  }, [enabled, email, tripId, q.data, qc]);

  return { data: q.data };
}

export function useTripActivities(tripId: string | undefined) {
  return useQuery<Activity[]>({
    queryKey: queryKeys.activities(tripId),
    enabled: !!tripId,
    queryFn: async () => {
      return unwrap(getTripActivities(tripId!));
    },
  });
}
